import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import DataTable from "react-data-table-component";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { auctionServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { Loader } from "../../components/CustomLoader";
import CustomPagination from "../../components/CustomPagination";
import TableFilter from "../../components/shared/TableFilter";

const BidControlManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();
  
  // Extract type from pathname if not in params
  const getTypeFromPath = () => {
    if (type) return type;
    const pathParts = location.pathname.split('/');
    const managementIndex = pathParts.indexOf('bid-controller-management');
    if (managementIndex !== -1 && pathParts[managementIndex + 1]) {
      const pathType = pathParts[managementIndex + 1];
      if (['auction', 'market-dropai', 'claimpoint'].includes(pathType)) {
        return pathType;
      }
    }
    return null;
  };

  const currentType = getTypeFromPath();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [auctionStatusFilter, setAuctionStatusFilter] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  
  // Tab values mapping
  const tabValues = {
    "normal": 0,
    "marketDropAi": 1,
    "claimPoint": 2,
  };
  
  // Get current tab value based on URL type
  const getCurrentTabValue = () => {
    if (currentType === "auction") return "normal";
    if (currentType === "market-dropai") return "marketDropAi";
    if (currentType === "claimpoint") return "claimPoint";
    return "normal"; // Default
  };
  
  const [selectedTabValue, setSelectedTabValue] = useState(getCurrentTabValue());

  // Redirect to default type if no type specified
  useEffect(() => {
    if (!currentType && location.pathname === '/bid-controller-management') {
      navigate('/bid-controller-management/auction', { replace: true });
      return;
    }
    // Update selected tab value when route changes
    setSelectedTabValue(getCurrentTabValue());
  }, [currentType, location.pathname, navigate]);

  // Fetch auctions when page, search, filters, or tab changes
  useEffect(() => {
    if (currentType || location.pathname.includes('bid-controller-management')) {
      fetchAuctionsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, selectedTabValue, currentType, location.pathname, statusFilter, auctionStatusFilter]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1);
        fetchAuctionsData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    event.preventDefault();
    const valueMap = {
      0: { value: "normal", route: "auction" },
      1: { value: "marketDropAi", route: "market-dropai" },
      2: { value: "claimPoint", route: "claimpoint" },
    };
    const selected = valueMap[newValue];
    
    // Set the tab value
    setSelectedTabValue(selected.value);
    
    // Reset search and filters
    setSearchTerm("");
    setStatusFilter([]);
    setAuctionStatusFilter([]);
    
    navigate(`/bid-controller-management/${selected.route}`, { replace: true });
  };

  const getCurrentTabIndex = () => {
    if (currentType === "auction") return 0;
    if (currentType === "market-dropai") return 1;
    if (currentType === "claimpoint") return 2;
    return 0;
  };

  const fetchAuctionsData = async () => {
    try {
      setShowLoader(true);
      setError(null);

      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm || "",
        auctionType: selectedTabValue ? [selectedTabValue] : undefined,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        auctionStatus: auctionStatusFilter.length > 0 ? auctionStatusFilter : undefined,
      };
      
      const response = await auctionServices.getAllAuctions(params);
      
      // Show loader for minimum 1 second for better UX
      setTimeout(() => {
        if (response?.data?.data?.auctions) {
          setAuctions(response.data.data.auctions);
          setTotalCount(response.data.data.total || 0);
        }
        setShowLoader(false);
      }, 1000);
      
    } catch (error) {
      setError(error.message);
      setShowLoader(false);
      errorHandler(error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuctionStatusChip = (auction) => {
    if (!auction) return null;

    if (auction.auctionStatus == "Cancelled") {
      return <Chip label="Cancelled" color="error" size="small" />;
    }
    if (auction.auctionStatus == "Upcoming") {
      return <Chip label="Upcoming" color="info" size="small" />;
    } else if (auction.auctionStatus == "Live") {
      return <Chip label="Live" color="success" size="small" />;
    } else if (auction.auctionStatus == "Paused") {
      return <Chip label="Paused" color="warning" size="small" />;
    } else if (auction.auctionStatus == "Stopped") {
      return <Chip label="Stopped" color="error" size="small" />;
    }else {
      return <Chip label="Completed" color="default" size="small" />;
    }
  };

  const columns = [
    {
      name: "S.No.",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "80px",
      center: true,
    },
    {
      name: "Vehicle Name",
      cell: (row) => (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {row?.vehicle?.make?.name + " " + row?.vehicle?.model}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Auction Type",
      cell: (row) => (
        <Chip
          label={row.auctionType === "normal" ? "Normal Auction" : capitalize(row.auctionType)}
          size="small"
          sx={{
            bgcolor: "#ff9800",
            color: "white",
            fontWeight: 500,
            "& .MuiChip-label": {
              color: "white",
            },
          }}
        />
      ),
      center: true,
    },
    {
      name: "Original Price",
      cell: (row) => (
        <Typography variant="body2">
          {formatCurrency(row?.vehicle?.originalPrice || 0)}
        </Typography>
      ),
      center: true,
    },
    {
      name: "Current Bid",
      cell: (row) => {
        // Don't show current bid if status is Upcoming
        if (row.auctionStatus === "Upcoming") {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        return row.currentBid ? (
          <Typography variant="body2" sx={{ color: "#4caf50", fontWeight: 600 }}>
            {formatCurrency(row.currentBid)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No bids
          </Typography>
        );
      },
      center: true,
    },
    {
      name: "Start Date",
      cell: (row) => (
        <Typography variant="body2">
          {formatDate(row.schedule?.startTime)}
        </Typography>
      ),
    },
    {
      name: "End Date",
      cell: (row) => (
        <Typography variant="body2">
          {formatDate(row.schedule?.endTime)}
        </Typography>
      ),
    },
    {
      name: "Auction Status",
      cell: (row) => (
        <Box className="bid-controller-status-container">
          {getAuctionStatusChip(row)}
        </Box>
      ),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        const isUpcoming = row.auctionStatus === "Upcoming";
        return (
          <Box className="bid-controller-actions-container">
            <Tooltip title={isUpcoming ? "View disabled for upcoming auctions" : "View"}>
              <span>
                <IconButton
                  size="small"
                  className="bid-controller-view-button"
                  onClick={() => {
                    // if (isUpcoming) return;
                    const auctionType = row?.auctionType;
                    let typeParam = "";
                    if (auctionType === "normal") typeParam = "auction";
                    else if (auctionType === "marketDropAi") typeParam = "market-dropai";
                    else if (auctionType === "claimPoint") typeParam = "claimpoint";
                    
                    navigate(`/bid-controller-management/view/${row._id}/${typeParam}`);
                  }}
                  // disabled={isUpcoming}
               
                >
                  <ViewIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
      width: "100px",
      center: true,
    },
  ];


  return (
    <Box className="bid-controller-container">
      {/* Header Section */}
      <Card className="bid-controller-card">
      <CardHeader 
          title="Bid Controller Management"
          className="bid-controller-header"
          titleTypographyProps={{ className: "bid-controller-header-title" }}
      />
      </Card>
      
      {/* Tabs Section */}
      <Card className="bid-controller-card">
        <Box className="bid-controller-tabs-container">
        <Tabs
          value={getCurrentTabIndex()}
          onChange={handleTabChange}
          aria-label="auction type tabs"
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
            className="bid-controller-tabs"
        >
          <Tab 
            label={
              <Box className="bid-controller-tab-label">
                <Chip 
                  label="Normal Auction" 
                  color={currentType === "auction" ? "primary" : "default"} 
                  size="small"
                  className={currentType === "auction" ? "bid-controller-chip-active" : "bid-controller-chip-inactive"}
                />
                <Typography 
                  variant="body2" 
                  className={currentType === "auction" ? "bid-controller-tab-text-active" : "bid-controller-tab-text-inactive"}
                >
                  Bid History
                </Typography>
              </Box>
            }
          />
          <Tab 
            label={
              <Box className="bid-controller-tab-label">
                <Chip 
                  label="MarketDropAi" 
                  color={currentType === "market-dropai" ? "info" : "default"} 
                  size="small"
                  className={currentType === "market-dropai" ? "bid-controller-chip-info-active" : "bid-controller-chip-inactive"}
                />
                <Typography 
                  variant="body2" 
                  className={currentType === "market-dropai" ? "bid-controller-tab-text-info-active" : "bid-controller-tab-text-inactive"}
                >
                  Winners
                </Typography>
              </Box>
            }
          />
          <Tab 
            label={
              <Box className="bid-controller-tab-label">
                <Chip 
                  label="ClaimPoint" 
                  color={currentType === "claimpoint" ? "success" : "default"} 
                  size="small"
                  className={currentType === "claimpoint" ? "bid-controller-chip-success-active" : "bid-controller-chip-inactive"}
                />
                <Typography 
                  variant="body2" 
                  className={currentType === "claimpoint" ? "bid-controller-tab-text-success-active" : "bid-controller-tab-text-inactive"}
                >
                  Bids & Winners
                </Typography>
              </Box>
            }
          />
        </Tabs>
      </Box>
      </Card>

      {/* Table Section */}
      <Card className="bid-controller-card">
        {/* Search and Filters Section */}
        <Box className="bid-controller-search-container" display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" p={2} gap={2}>
          <TableFilter
            data={[
              { label: "Active", value: 1 },
              { label: "Inactive", value: 0 }
            ]}
            value={statusFilter}
            setValue={setStatusFilter}
            filterName="Status"
          />
          <TableFilter
            data={[
              { label: "Upcoming", value: "upcoming" },
              { label: "Live", value: "live" },
              { label: "Completed", value: "completed" },
              { label: "Cancelled", value: "cancelled" },
              { label: "Paused", value: "paused" },
            { label: "Stopped", value: "stopped" }
            ]}
            value={auctionStatusFilter}
            setValue={setAuctionStatusFilter}
            filterName="Auction Status"
          />
          <TextField
            value={searchTerm}
            placeholder="Search"
            onChange={handleSearch}
            size="small"
            variant="outlined"
            className="bid-controller-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="bid-controller-search-icon" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    className="bid-controller-clear-button"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <CardContent className="bid-controller-table-content">
          {error && (
            <Box className="bid-controller-error-box">
              <Typography className="bid-controller-error-text">{error}</Typography>
            </Box>
          )}
          <Box className={error ? "bid-controller-table-wrapper bid-controller-table-wrapper-with-error" : "bid-controller-table-wrapper bid-controller-table-wrapper-no-error"}>
            <DataTable
              columns={columns}
              data={auctions}
              pagination
              paginationServer
              paginationTotalRows={totalCount}
              onChangePage={handlePageChange}
              paginationPerPage={pageSize}
              onChangeRowsPerPage={(rows) => setPageSize(rows)}
              progressPending={showLoader}
              progressComponent={<Loader />}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationComponent={(props) => (
                <CustomPagination {...props} currentPage={currentPage} />
              )}
              highlightOnHover
              pointerOnHover
              customStyles={{
                table: {
                  style: {
                    backgroundColor: "transparent",
                  },
                },
                headRow: {
                  style: {
                    backgroundColor: "#f5f5f5",
                    borderTop: "none",
                    borderBottom: "2px solid #e0e0e0",
                    minHeight: "48px",
                  },
                },
                headCells: {
                  style: {
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    color: "#424242",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                  },
                },
                cells: {
                  style: {
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    fontSize: "0.875rem",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                    color: "#424242",
                  },
                },
                rows: {
                  style: {
                    borderBottom: "1px solid #e0e0e0",
                    transition: "background-color 0.2s ease",
                    "&:nth-of-type(even)": {
                      backgroundColor: "#fafafa",
                    },
                    "&:hover": {
                      backgroundColor: "#f0f0f0 !important",
                    },
                  },
                },
                pagination: {
                  style: {
                    borderTop: "1px solid",
                    borderColor: "divider",
                    paddingTop: "16px",
                    marginTop: "16px",
                  },
                },
              }}
            />
          </Box>
        </CardContent>
    </Card>
    </Box>
  );
};

export default BidControlManagement;
